import { useQuery } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface MetricsData {
  kpis: {
    uniqueVisitors: { current: number; growth: number; label: string };
    chatSessions: { current: number; growth: number; label: string };
    bookings: { current: number; growth: number; label: string };
    conversionRate: { current: number; growth: null; label: string };
    avgResponseTime: { current: number; growth: null; label: string };
  };
  revenue: {
    mrr: number;
    arr: number;
    totalUsers: number;
    payingUsers: number;
    trialUsers: number;
    canceledUsers: number;
    arpu: number;
    newSignups: number;
    signupGrowth: number;
    trialConversionRate: number;
    churnRate: number;
    totalAgents: number;
    avgAgentsPerUser: number;
  };
  charts: {
    topServices: Array<{ service: string; bookings: number }>;
    trafficSources: Array<{ source: string; count: number }>;
    userTypes: Array<{ type: string; count: number }>;
  };
  recentActivity: Array<{
    timestamp: string;
    event: string;
    details: string;
  }>;
  systemHealth: {
    score: number;
    status: string;
  };
}

interface LiveStats {
  activeVisitors: number;
  totalVisitorsToday: number;
  currentResponseTime: number;
  slowRequestsToday: number;
  systemUptime: number;
  memoryUsage: number;
}

const apiCall = async (endpoint: string) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No auth token');
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const useMetricsSummary = (timeframe: 'day' | 'week' | 'month' = 'week') => {
  return useQuery<MetricsData>({
    queryKey: ['metrics-summary', timeframe],
    queryFn: () => apiCall(`/api/admin-frontend/metrics-summary?timeframe=${timeframe}`),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useLiveStats = () => {
  return useQuery<LiveStats>({
    queryKey: ['live-stats'],
    queryFn: () => apiCall('/api/admin-frontend/live-stats'),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useAdoptionMetrics = () => {
  return useQuery({
    queryKey: ['adoption-metrics'],
    queryFn: () => apiCall('/api/admin-frontend/adoption-metrics'),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    refetchInterval: 60 * 60 * 1000, // Refetch every hour
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export const useExportData = () => {
  const exportMetrics = async (
    timeframe: 'day' | 'week' | 'month' = 'week',
    format: 'csv' | 'json' = 'csv',
    includeAdoption: boolean = true
  ) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${API_BASE}/api/admin-frontend/export-data?format=${format}&timeframe=${timeframe}&includeAdoption=${includeAdoption}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      if (format === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `svarra-metrics-${timeframe}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `svarra-metrics-${timeframe}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  };

  return { exportMetrics };
};